import { ButtonProps } from "@mui/material/Button/Button";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import Stepper from "@mui/material/Stepper";
import { Formik, FormikProps } from "formik";
import * as Yup from "yup";
import { useCallback, useMemo, useRef, useState } from "react";
import { Dialog } from "../../../components/Dialog";
import { Actions } from "../../../components/CampaignAwarded/Actions";
import { Public } from "../../../components/CampaignAwarded/Public";
import { Review } from "../../../components/CampaignAwarded/Review";
import { useToast } from "../../../hooks/useToast";
import { useStyles } from "../styles";
import { map } from "lodash";
import { useMutation } from "react-query";
import { queryClient } from "../../../services/queryClient";
import {
  CampaignAwarded,
  UpdateCampaignAwardedData,
} from "../../../types/campaignAwarded";
import { updateCampaignsAwarded } from "../../../services/campaignAwarded";

type EditAwardedProps = {
  data: CampaignAwarded;
  open: boolean;
  onClose: () => void;
};

const validationSchema = Yup.object().shape({
  action: Yup.string().required("Ação inválida"),
  name: Yup.string().required("Nome obrigatório"),
  top_ranking: Yup.number()
    .min(1, "Ao menos uma pessoa deve ser premiada")
    .required("Ao menos uma pessoa deve ser premiada"),
  target: Yup.object().required(
    "Ao menos um público deve participar da premiação"
  ),
});

export function EditAwarded({ onClose, open, data }: EditAwardedProps) {
  const formikRef = useRef<FormikProps<UpdateCampaignAwardedData>>(null);

  const toast = useToast();
  const styles = useStyles();
  const [activeStep, setActiveStep] = useState<number>(0);

  const editCampaignAwarded = useMutation(
    async (data: UpdateCampaignAwardedData) => {
      const response = await updateCampaignsAwarded(data);
      return response.message;
    },
    {
      onSuccess: () => {
        toast.show("Ação atualizada com sucesso!", { severity: "success" });
        queryClient.invalidateQueries(["campaignsAwarded"]);
        handleClose();
      },
      onError: () => {
        toast.show("Erro ao editar a ação premiada", { severity: "error" });
      },
    }
  );

  const steps = useMemo(() => {
    return ["Ações", "Público", "Resumo"];
  }, []);

  const handleSave = useCallback(
    async (data: UpdateCampaignAwardedData) => {
      await editCampaignAwarded.mutateAsync(data);
    },
    [editCampaignAwarded]
  );

  const handleClose = useCallback(() => {
    setActiveStep(0);
    onClose();
  }, [onClose, setActiveStep]);

  const handleValidateStep = useCallback((): Promise<boolean> => {
    return new Promise(async (resolve, reject) => {
      try {
        switch (activeStep) {
          case 0:
            await formikRef.current?.validateField("action");
            break;
          case 1:
            await formikRef.current?.validateField("name");
            await formikRef.current?.validateField("top_ranking");
            await formikRef.current?.validateField("target");
            break;
        }

        resolve(formikRef.current?.isValid);
      } catch (err) {
        reject(false);
      }
    });
  }, [activeStep, formikRef]);

  const handleGoBack = useCallback(() => {
    formikRef.current?.setErrors({});
    setActiveStep((prev) => prev - 1);
  }, []);

  const handleGoNext = useCallback(async () => {
    try {
      const isValid = await handleValidateStep();
      if (isValid) {
        setActiveStep((prev) => prev + 1);
      }
    } catch (err) {
      console.log(err);
    }
  }, [handleValidateStep]);

  const actions: ButtonProps[] = useMemo(() => {
    return [
      {
        children: "Voltar",
        onClick: () => handleGoBack(),
        disabled: activeStep === 0 || editCampaignAwarded.isLoading,
      },
      {
        disabled: editCampaignAwarded.isLoading,
        children: editCampaignAwarded.isLoading
          ? "Enviado solicitação..."
          : activeStep === 2
          ? "Atualizar"
          : "Continuar",

        variant: "contained",
        onClick: () =>
          activeStep === 2 ? formikRef.current?.handleSubmit() : handleGoNext(),
      },
    ];
  }, [
    activeStep,
    formikRef,
    editCampaignAwarded.isLoading,
    handleGoNext,
    handleGoBack,
  ]);

  const renderContent = useCallback(() => {
    switch (activeStep) {
      case 0:
        return <Actions />;
      case 1:
        return <Public data={data} />;
      case 2:
        return <Review />;
      default:
        return <></>;
    }
  }, [activeStep, data]);

  return (
    <Dialog
      fullWidth
      maxWidth="md"
      title={data?.name}
      open={open}
      actions={actions}
      onClose={handleClose}
    >
      <Stepper activeStep={activeStep}>
        {map(steps, (step) => {
          return (
            <Step key={step}>
              <StepLabel>{step}</StepLabel>
            </Step>
          );
        })}
      </Stepper>
      <div className={styles.rootContent}>
        <Formik
          innerRef={formikRef}
          initialValues={
            {
              id: data?.id,
              action: data?.type,
              public: data?.form_id ? "form" : "insight",
              top_ranking: data?.top_ranking,
              name: data?.name,
            } as UpdateCampaignAwardedData
          }
          validationSchema={validationSchema}
          validateOnChange={false}
          onSubmit={handleSave}
        >
          {renderContent()}
        </Formik>
      </div>
    </Dialog>
  );
}
